import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Upload, Palette, Download, ArrowRight, Check, Zap, Image as ImageIcon, Users } from "lucide-react";
import { Button } from "../components/ui/button";

const LandingPage = () => {
    const features = [
        {
            icon: <Upload className="w-6 h-6" />,
            title: "Upload Product",
            description: "Upload foto produk Anda dengan mudah. Mendukung berbagai format gambar."
        },
        {
            icon: <Palette className="w-6 h-6" />,
            title: "Pilih Gaya",
            description: "Pilih background, model, dan gaya pencahayaan sesuai kebutuhan."
        },
        {
            icon: <Sparkles className="w-6 h-6" />,
            title: "AI Generate",
            description: "Biarkan AI menciptakan foto produk profesional dalam hitungan detik."
        },
        {
            icon: <Download className="w-6 h-6" />,
            title: "Download & Share",
            description: "Unduh hasil atau bagikan langsung ke media sosial."
        }
    ];

    const pricingPlans = [
        {
            name: "Starter",
            price: "Rp 50.000",
            credits: 50,
            features: ["50 Credits", "Basic Backgrounds", "Standard Quality", "Email Support"]
        },
        {
            name: "Professional",
            price: "Rp 150.000",
            credits: 200,
            popular: true,
            features: ["200 Credits", "All Backgrounds", "HD Quality", "Model Selection", "Priority Support"]
        },
        {
            name: "Enterprise",
            price: "Rp 500.000",
            credits: 1000,
            features: ["1000 Credits", "All Features", "4K Quality", "Custom Models", "Dedicated Support"]
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-heading font-bold text-xl tracking-tight">mangoplus.ai</span>
                        </Link>
                        <div className="flex items-center gap-4">
                            <Link to="/login">
                                <Button variant="ghost" data-testid="nav-login-btn">Masuk</Button>
                            </Link>
                            <Link to="/register">
                                <Button className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600" data-testid="nav-register-btn">
                                    Mulai Gratis
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-20 px-4 md:px-8 lg:px-12">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 text-red-600 text-sm font-medium mb-6">
                                <Zap className="w-4 h-4" />
                                Powered by AI
                            </span>
                            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
                                Foto Produk <span className="gradient-text">Profesional</span> dalam Hitungan Detik
                            </h1>
                            <p className="text-lg text-gray-600 mb-8 max-w-lg">
                                Transformasi foto produk biasa menjadi gambar berkualitas studio dengan teknologi AI. 
                                Cocok untuk e-commerce, sosial media, dan katalog produk.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/register">
                                    <Button size="lg" className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-lg px-8" data-testid="hero-cta-btn">
                                        Mulai Generate <ArrowRight className="ml-2 w-5 h-5" />
                                    </Button>
                                </Link>
                                <Button size="lg" variant="outline" className="rounded-full text-lg px-8" data-testid="hero-demo-btn">
                                    Lihat Demo
                                </Button>
                            </div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl shadow-red-500/20">
                                <img
                                    src="https://images.unsplash.com/photo-1580744948109-b2da1320ac87?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                                    alt="Product Photography"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-gray-100">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                                        <Check className="w-6 h-6 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-900">AI Generated</p>
                                        <p className="text-sm text-gray-500">dalam 30 detik</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-20 px-4 md:px-8 lg:px-12 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">
                            Cara Kerja
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Hanya 4 langkah mudah untuk mendapatkan foto produk profesional
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200"
                            >
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center text-white mb-4">
                                    {feature.icon}
                                </div>
                                <div className="text-sm font-medium text-gray-400 mb-2">STEP {index + 1}</div>
                                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                                <p className="text-gray-600">{feature.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Bento */}
            <section className="py-20 px-4 md:px-8 lg:px-12">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">
                            Fitur Unggulan
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="md:col-span-2 bg-gradient-to-br from-red-500 to-orange-500 rounded-3xl p-8 text-white">
                            <ImageIcon className="w-12 h-12 mb-6 opacity-90" />
                            <h3 className="font-heading text-2xl font-bold mb-3">AI Background Generation</h3>
                            <p className="text-white/80 text-lg">
                                Pilih dari ratusan background profesional atau biarkan AI membuat background custom sesuai kebutuhan Anda.
                            </p>
                        </div>
                        <div className="bg-gray-900 rounded-3xl p-8 text-white">
                            <Users className="w-12 h-12 mb-6 opacity-90" />
                            <h3 className="font-heading text-2xl font-bold mb-3">Model Virtual</h3>
                            <p className="text-white/80">
                                Tambahkan model virtual untuk foto fashion atau lifestyle product.
                            </p>
                        </div>
                        <div className="bg-yellow-400 rounded-3xl p-8">
                            <Sparkles className="w-12 h-12 mb-6 text-yellow-800" />
                            <h3 className="font-heading text-2xl font-bold mb-3 text-yellow-900">Studio Lighting</h3>
                            <p className="text-yellow-800">
                                Atur pencahayaan profesional untuk hasil foto yang sempurna.
                            </p>
                        </div>
                        <div className="md:col-span-2 bg-gray-100 rounded-3xl p-8 overflow-hidden relative">
                            <img 
                                src="https://images.unsplash.com/photo-1590415914114-935b37182a76?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                                alt="Studio"
                                className="absolute inset-0 w-full h-full object-cover opacity-30"
                            />
                            <div className="relative z-10">
                                <h3 className="font-heading text-2xl font-bold mb-3 text-gray-900">Export HD & 4K</h3>
                                <p className="text-gray-700 text-lg max-w-md">
                                    Download hasil dalam berbagai resolusi, dari sosial media hingga cetak berkualitas tinggi.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section className="py-20 px-4 md:px-8 lg:px-12 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">
                            Pilih Paket
                        </h2>
                        <p className="text-lg text-gray-600">
                            Bayar sesuai kebutuhan. Tanpa langganan bulanan.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                        {pricingPlans.map((plan, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className={`relative bg-white rounded-3xl p-8 border ${plan.popular ? 'border-red-500 shadow-xl shadow-red-500/10' : 'border-gray-200'}`}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                        <span className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-medium px-4 py-1 rounded-full">
                                            Paling Populer
                                        </span>
                                    </div>
                                )}
                                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">{plan.name}</h3>
                                <div className="mb-6">
                                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                                </div>
                                <ul className="space-y-3 mb-8">
                                    {plan.features.map((feature, i) => (
                                        <li key={i} className="flex items-center gap-3 text-gray-600">
                                            <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                                <Link to="/register">
                                    <Button 
                                        className={`w-full rounded-full ${plan.popular ? 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600' : ''}`}
                                        variant={plan.popular ? 'default' : 'outline'}
                                        data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                                    >
                                        Pilih {plan.name}
                                    </Button>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 px-4 md:px-8 lg:px-12">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="bg-gradient-to-r from-red-500 to-orange-500 rounded-3xl p-12 text-white"
                    >
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
                            Siap Meningkatkan Kualitas Foto Produk?
                        </h2>
                        <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
                            Daftar sekarang dan dapatkan 10 credits gratis untuk mencoba semua fitur.
                        </p>
                        <Link to="/register">
                            <Button size="lg" className="rounded-full bg-white text-red-600 hover:bg-gray-100 text-lg px-8" data-testid="cta-register-btn">
                                Daftar Gratis <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4 md:px-8 lg:px-12 border-t border-gray-100">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-heading font-bold text-xl tracking-tight">mangoplus.ai</span>
                        </div>
                        <p className="text-gray-500 text-sm">
                            © 2025 mangoplus.ai. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
